import { UploadService } from "@/services/APIs";
import {
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { Button, Upload } from "antd";
import type { UploadProps, UploadFile } from "antd/es/upload";
import { useEffect, useState, useRef } from "react";

export const CustomFileUpload = (
  props: UploadProps & {
    action: string;
    value?: File[] | string[];
    onChange?: (value: File[] | string[]) => void;
    defaultFileList?: File[];
  }
) => {
  const { value = [], onChange, ...restProps } = props;

  console.log(value, "value");

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any>([]);
  const previousValueRef = useRef<string>("");

  // Parse and set uploaded files from value prop
  useEffect(() => {
    try {
      let parsedValue = value;

      // Handle undefined/null/empty
      if (!value || (Array.isArray(value) && value.length === 0)) {
        const emptyArrayStr = JSON.stringify([]);
        if (previousValueRef.current !== emptyArrayStr) {
          previousValueRef.current = emptyArrayStr;
          setUploadedFiles([]);
        }
        return;
      }

      // Handle string values (could be JSON string or single URL/ID)
      if (typeof value === "string") {
        try {
          parsedValue = JSON.parse(value);
        } catch {
          // Not JSON, treat as single URL/ID string
          parsedValue = value.trim() ? [value] : [];
        }
      }

      // Ensure it's an array
      if (!Array.isArray(parsedValue)) {
        parsedValue = parsedValue ? [parsedValue] : [];
      }

      // Filter out null/undefined/empty values
      const filteredValue = parsedValue.filter(
        item => item !== null && item !== undefined && item !== ""
      );

      // Only update if the value actually changed
      const newValueStr = JSON.stringify(filteredValue);

      if (previousValueRef.current !== newValueStr) {
        previousValueRef.current = newValueStr;
        console.log(
          "CustomFileUpload: Setting uploaded files from value:",
          filteredValue
        );
        setUploadedFiles(filteredValue);
      }
    } catch (e) {
      console.error("Error parsing uploaded files:", e, "value:", value);
      const emptyArrayStr = JSON.stringify([]);
      if (previousValueRef.current !== emptyArrayStr) {
        previousValueRef.current = emptyArrayStr;
        setUploadedFiles([]);
      }
    }
  }, [value]);

  const handleFileSelect = async (file: File) => {
    const newFiles = [...selectedFiles, file];
    setSelectedFiles(newFiles);
    try {
      const response = await UploadService.uploadFiles({
        folderName: "uploads",
        files: newFiles,
      });

      if (response && Array.isArray(response)) {
        const uploadedUrls = response;
        const safeValue = Array.isArray(value) ? value : [];
        const newValue = [
          ...safeValue.filter(item => typeof item === "string"),
          ...uploadedUrls,
        ];

        onChange?.(newValue);
        setUploadedFiles(newValue);
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
    return false;
  };

  const handleRemove = (file: any) => {
    const fileToRemove = file.file_path ?? file.url ?? file.name;
    const filteredFiles = uploadedFiles.filter(
      (f: any) => (f.file_path ?? f.url ?? f.name) !== fileToRemove
    );

    setUploadedFiles(filteredFiles);
    setSelectedFiles([]);
    onChange?.(filteredFiles);
  };

  const handleChange = (info: any) => {
    if (info.file.status === "removed") {
      handleRemove(info.file);
    }
  };

  const handlePreview = (file: any) => {
    const fileUrl = file.url ?? file.file_path;
    if (!fileUrl) {
      return;
    }

    const fileExtension = fileUrl.split(".").pop()?.toLowerCase();
    if (["png", "jpg", "jpeg", "gif", "webp"].includes(fileExtension!)) {
      window.open(fileUrl, "_blank");
    } else {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.target = "_blank";
      link.download = fileUrl.split("/").pop() ?? "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const fileList: UploadFile[] =
    uploadedFiles?.map((file: any, index: number) => {
      // Handle file objects with properties
      if (typeof file === "object" && file !== null) {
        const fileUrl =
          file?.file_path ?? file?.url ?? file?.file_path_optimized;
        const fileName =
          file?.file_original_name ?? file?.name ?? file?.file_name ?? "file";
        const fileId = file?.id ?? file?.uid ?? `rc-upload-${index}`;

        if (fileUrl) {
          return {
            uid: String(fileId),
            name: fileName,
            status: "done" as const,
            url: fileUrl,
          };
        }
      }

      // Handle string values (URLs or IDs)
      if (typeof file === "string" && file.trim()) {
        // If it looks like a URL, use it directly
        if (
          file.startsWith("http://") ||
          file.startsWith("https://") ||
          file.startsWith("/")
        ) {
          return {
            uid: `rc-upload-${index}`,
            name: file.split("/").pop() ?? "file",
            status: "done" as const,
            url: file,
          };
        }
        // Otherwise, it might be an ID - we'll need to fetch the file info
        // For now, just display it as-is
        return {
          uid: `rc-upload-${index}`,
          name: `File ${index + 1}`,
          status: "done" as const,
          url: file,
        };
      }

      // Fallback for invalid files
      return {
        uid: `rc-upload-${index}`,
        name: "Invalid file",
        status: "error" as const,
        url: "",
      };
    }) ?? [];

  return (
    <div className="space-y-2">
      <Upload
        {...restProps}
        beforeUpload={handleFileSelect}
        onChange={handleChange}
        onRemove={handleRemove}
        maxCount={1}
        fileList={fileList}
        showUploadList={{
          showRemoveIcon: true,
          showDownloadIcon: true,
          showPreviewIcon: true,
          downloadIcon: <DownloadOutlined />,
          removeIcon: <DeleteOutlined />,
          previewIcon: <EyeOutlined />,
        }}
        onPreview={handlePreview} // 👈 handle click event on file name or icon
      >
        <Button icon={<UploadOutlined />}>Upload File</Button>
      </Upload>
    </div>
  );
};
